import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuTop from "../../../components/menu/top/menu-top";
import InfoComponent from "../../../components/info/info";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import Form from "../../../components/form/form";
import { useAlert } from "../../../components/alert/alert";
import { SEVERITY } from "../../../shared/interfaces/alert";
import { FormProps, FormTypes } from "../../../shared/interfaces/form";
import { fetchCompaniesService, fetchUserCompanyService, updateUserCompanyService } from "../../../features/company/service/company";
import { fetchNotificationRolesService } from "../../../shared/services/roles";
import { Company } from "../../../shared/interfaces/company";
import { fetchUserByIdService } from "../../../features/user/service/user-service";
import "./style.css";

const buildNotificationSettingsFormData = ({
    isSuperAdmin,
    companyOptions,
    roleOptions,
    selectedCompanyId,
    companyData
}: {
    isSuperAdmin: boolean;
    companyOptions: any[];
    roleOptions: any[];
    selectedCompanyId?: string;
    companyData?: Company | null;
}): FormProps[] => {
    const fields: FormProps[] = [];

    if (isSuperAdmin) {
        fields.push({
            typeOfField: FormTypes.SELECT,
            type: "text",
            placeholder: "Cliente*",
            value: selectedCompanyId || null,
            name: "companyId",
            rules: {
                required: "Cliente é obrigatório"
            },
            width: 1000,
            height: 55,
            fullWidth: true,
            options: {
                placeholder: "Cliente",
                data: companyOptions,
                value: selectedCompanyId || null
            }
        });
    }

    fields.push({
        typeOfField: FormTypes.SELECT,
        type: "text",
        placeholder: "Tipos de usuário que recebem notificações",
        value: companyData?.notificationRecipientRoleIds || [],
        name: "notificationRecipientRoleIds",
        width: 1000,
        height: 55,
        fullWidth: true,
        disabled: isSuperAdmin && !selectedCompanyId,
        options: {
            placeholder: "Tipos de usuário que recebem notificações",
            data: roleOptions,
            value: companyData?.notificationRecipientRoleIds || [],
            multiple: true
        }
    });

    return fields;
};

export function NotificationSettingsPage() {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const [isLoading, setIsLoading] = useState(true);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(undefined);
    const [companyOptions, setCompanyOptions] = useState<any[]>([]);
    const [roleOptions, setRoleOptions] = useState<any[]>([]);
    const [companyData, setCompanyData] = useState<Company | null>(null);
    const [formData, setFormData] = useState<FormProps[]>([]);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    const pageTitle = useMemo(() => (
        isSuperAdmin ? "Configurar envio de notificações" : "Configurar notificações da empresa"
    ), [isSuperAdmin]);

    const rebuildFormData = useCallback((company?: Company | null, companyId?: string) => {
        setFormData(buildNotificationSettingsFormData({
            isSuperAdmin,
            companyOptions,
            roleOptions,
            selectedCompanyId: companyId,
            companyData: company || null
        }));
    }, [companyOptions, isSuperAdmin, roleOptions]);

    const loadCompanySettings = useCallback(async (companyId?: string) => {
        if (!companyId) {
            setCompanyData(null);
            rebuildFormData(null, undefined);
            return;
        }

        try {
            const companyResponse = await fetchUserCompanyService(companyId);
            setCompanyData(companyResponse);
            rebuildFormData(companyResponse, companyId);
        } catch (error) {
            console.error("Erro ao buscar configuração da empresa:", error);
            showAlert("Erro ao carregar configuração da empresa", SEVERITY.ERROR);
        }
    }, [rebuildFormData, showAlert]);

    const fetchInitialData = useCallback(async () => {
        if (!user?.id) {
            navigate("/unauthorized", { replace: true });
            return;
        }

        setIsLoading(true);

        try {
            const currentUser = await fetchUserByIdService(user.id);
            const roleDescription = String(currentUser?.role?.description || currentUser?.role?.name || "").toLowerCase();
            const currentIsSuperAdmin = roleDescription.includes("super-admin");
            const currentIsFinancialUser = roleDescription.includes("financeiro");

            if (!currentIsSuperAdmin && !currentIsFinancialUser) {
                navigate("/unauthorized", { replace: true });
                return;
            }

            setIsSuperAdmin(currentIsSuperAdmin);
            const [roles, companies] = await Promise.all([
                fetchNotificationRolesService(),
                currentIsSuperAdmin ? fetchCompaniesService() : Promise.resolve([])
            ]);

            const normalizedRoleOptions = roles?.map(({ id, name }: any) => ({ label: name, value: id })) || [];
            const normalizedCompanyOptions = companies?.map(({ id, name }: any) => ({ label: name, value: id })) || [];

            setRoleOptions(normalizedRoleOptions);
            setCompanyOptions(normalizedCompanyOptions);

            if (!currentIsSuperAdmin && user?.company?.id) {
                const companyResponse = await fetchUserCompanyService(user.company.id);
                setCompanyData(companyResponse);
                setSelectedCompanyId(user.company.id);
                setFormData(buildNotificationSettingsFormData({
                    isSuperAdmin: false,
                    companyOptions: normalizedCompanyOptions,
                    roleOptions: normalizedRoleOptions,
                    selectedCompanyId: user.company.id,
                    companyData: companyResponse
                }));
                return;
            }

            setFormData(buildNotificationSettingsFormData({
                isSuperAdmin: currentIsSuperAdmin,
                companyOptions: normalizedCompanyOptions,
                roleOptions: normalizedRoleOptions,
                selectedCompanyId: undefined,
                companyData: null
            }));
        } catch (error) {
            console.error("Erro ao carregar dados da configuração de notificações:", error);
            showAlert("Erro ao carregar dados da configuração de notificações", SEVERITY.ERROR);
        } finally {
            setIsLoading(false);
        }
    }, [navigate, showAlert, user?.company?.id, user?.id]);

    const handleFieldValueChange = useCallback((fieldName: string, value: any) => {
        if (fieldName !== "companyId" || !isSuperAdmin) {
            return;
        }

        const normalizedCompanyId = value || undefined;

        if (normalizedCompanyId === selectedCompanyId) {
            return;
        }

        setSelectedCompanyId(normalizedCompanyId);
        void loadCompanySettings(normalizedCompanyId);
    }, [isSuperAdmin, loadCompanySettings, selectedCompanyId]);

    const handleSubmit = async (values: any) => {
        const targetCompanyId = isSuperAdmin ? values?.companyId : user?.company?.id;

        if (!targetCompanyId) {
            showAlert("Selecione um cliente para configurar", SEVERITY.ERROR);
            return;
        }

        try {
            const baseCompany = companyData || await fetchUserCompanyService(targetCompanyId);
            const updatedCompany = await updateUserCompanyService(targetCompanyId, {
                ...baseCompany,
                notificationRecipientRoleIds: values?.notificationRecipientRoleIds || []
            });

            setCompanyData(updatedCompany);
            setSelectedCompanyId(targetCompanyId);
            rebuildFormData(updatedCompany, targetCompanyId);
            showAlert("Configuração de notificações salva com sucesso", SEVERITY.SUCCESS);
        } catch (error) {
            console.error("Erro ao salvar configuração de notificações:", error);
            showAlert("Erro ao salvar configuração de notificações", SEVERITY.ERROR);
        }
    };

    const handleCancel = useCallback(() => {
        rebuildFormData(companyData, selectedCompanyId);
    }, [companyData, rebuildFormData, selectedCompanyId]);

    useEffect(() => {
        void fetchInitialData();
    }, [fetchInitialData]);

    return (
        <div className="container-notification-settings">
            <MenuTop />
            <div className="container-notification-settings__content">
                <Breadcrumb
                    links={["Configurações", "Envio de notificações"]}
                    href={["", ""]}
                    onNavigate={navigate}
                />
                <InfoComponent />
                <Form
                    data={formData}
                    title={pageTitle}
                    onSubmitForm={handleSubmit}
                    onCancel={handleCancel}
                    isLoading={isLoading}
                    onFieldValueChange={handleFieldValueChange}
                />
            </div>
        </div>
    );
}

export default NotificationSettingsPage;
